import { Component } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import { getStorefrontDetails } from '~/actions/vendors/vendorStorefrontActions';
import { AppDispatch, RootState } from '~/reducers';
import { getCurrentStorefront } from '~/selectors/vendorAccountSelectors';
import { MappedAccountStorefront } from '~/types/mappedResponseTypes';
import { VendorStorefrontDetails } from '~/types/storefrontDetails';

import Loader from '../ui/loader/Loader';

interface WithVendorStorefrontProps {
  currentStorefront: MappedAccountStorefront;
  storefrontIsLoading: boolean;
  storefrontDetails: VendorStorefrontDetails | null;
}

interface DispatchProps {
  loadStorefront: (slug: string) => Promise<void | unknown>;
}

export interface InjectedStorefrontDetails {
  currentStorefront: MappedAccountStorefront;
  storefrontDetails: VendorStorefrontDetails;
}

const WithVendorStorefront = <Props extends InjectedStorefrontDetails>(
  WrappedComponent: React.ComponentType<Props>,
  requirePublication?: boolean
): React.ComponentType<Props & WithVendorStorefrontProps & DispatchProps> => {
  return class WrappedWithVendorStorefront extends Component<
    Props & WithVendorStorefrontProps & DispatchProps
  > {
    componentDidMount() {
      this.getStorefront();
    }

    componentDidUpdate(prevProps: Props) {
      const { currentStorefront } = this.props;
      if (currentStorefront.id !== prevProps.currentStorefront.id) {
        this.getStorefront();
      }
    }

    getStorefront = () => {
      const { currentStorefront, storefrontDetails, storefrontIsLoading, loadStorefront } =
        this.props;
      const storefrontNotLoaded = currentStorefront && !storefrontDetails;
      const storefrontChanged = storefrontDetails && storefrontDetails.id !== currentStorefront.id;
      const shouldLoadStorefront = storefrontNotLoaded || storefrontChanged;

      if (shouldLoadStorefront && !storefrontIsLoading) {
        loadStorefront(currentStorefront.slug).catch(() => null);
      }
    };

    render() {
      const { storefrontDetails, storefrontIsLoading } = this.props;

      if (!storefrontDetails || storefrontIsLoading) {
        return <Loader />;
      }

      if (requirePublication && !storefrontDetails.publishedAt) {
        window.location.replace('/inspire/vendors/dashboard');
        return null;
      }

      return <WrappedComponent {...this.props} />;
    }
  };
};

const mapStateToProps = (state: RootState) => ({
  currentStorefront: getCurrentStorefront(state) as MappedAccountStorefront,
  storefrontDetails: state.vendorStorefront.storefrontDetails,
  storefrontIsLoading: state.vendorStorefront.busy,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  loadStorefront: (slug: string) => dispatch(getStorefrontDetails(slug)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), WithVendorStorefront) as <
  Props extends Partial<InjectedStorefrontDetails> | Partial<RouteComponentProps>
>(
  WrappedComponent: React.ComponentType<Props>,
  requirePublication?: boolean
) => React.ComponentType<Props>;
